import React, { useEffect, useState } from "react";
import { api, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  User, Building2, Landmark, Bell, CalendarDays, Trophy, Loader2,
  ChevronLeft, ChevronRight, Clock,
} from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

function workDuration(join) {
  if (!join) return "—";
  const d0 = new Date(join);
  if (isNaN(d0)) return "—";
  const now = new Date();
  let months = (now.getFullYear() - d0.getFullYear()) * 12 + (now.getMonth() - d0.getMonth());
  let days = now.getDate() - d0.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  }
  if (months < 0) return "—";
  return `${months} m ${days} d`;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).replace(/ /g, "-");
}

const SectionCard = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-card rounded-lg border border-border overflow-hidden" data-testid={testid}>
    <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center gap-2">
      <Icon className="w-4 h-4" />
      <h3 className="font-heading font-semibold text-sm tracking-wide">{title}</h3>
    </div>
    <div className="divide-y divide-border">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex items-start px-4 py-3 gap-4 text-sm">
    <div className="w-32 shrink-0 text-muted-foreground">{label}</div>
    <div className="flex-1 text-foreground font-medium break-words">{value || "—"}</div>
  </div>
);

const EmptyPanel = ({ text }) => (
  <div className="py-12 text-center text-sm text-muted-foreground">{text}</div>
);

export default function EmployeeHome() {
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [stats, setStats] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(new Date());

  useEffect(() => {
    Promise.all([
      api.get("/employees/me").then((r) => r.data).catch(() => ({})),
      api.get("/dashboard/stats").then((r) => r.data).catch(() => ({})),
      api.get("/attendance").then((r) => r.data).catch(() => []),
    ]).then(([e, s, a]) => {
      setEmp(e || {});
      setStats(s || {});
      setAttendance(a || []);
      setLoading(false);
    });
  }, []);

  if (loading || !stats)
    return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  const avatar = emp?.avatar || user?.avatar;
  const presentDates = new Set(attendance.filter((a) => a.status === "Present").map((a) => a.date));
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const presentThisMonth = attendance.filter((a) => {
    const d = new Date(a.date);
    return a.status === "Present" && d.getFullYear() === new Date().getFullYear() && d.getMonth() === new Date().getMonth();
  }).length;

  // build calendar grid
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startPad = first.getDay();
  const total = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  const todayStr = new Date().toISOString().slice(0, 10);

  const shift = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  return (
    <div className="space-y-6" data-testid="employee-home-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left profile column */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-card rounded-lg border border-border p-5 flex flex-col items-center text-center">
            <div className="w-40 h-40 rounded-md bg-primary/10 text-primary flex items-center justify-center text-5xl font-bold overflow-hidden border border-border" data-testid="employee-home-photo">
              {avatar ? <img src={resolveAvatar(avatar)} alt={user?.name} className="w-full h-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground mt-4">{emp?.name || user?.name}</h2>
            <p className="text-sm text-muted-foreground">{emp?.designation || "—"}</p>
            <div className="mt-4 w-full bg-primary text-primary-foreground rounded-md py-2 text-sm font-semibold flex items-center justify-center gap-2" data-testid="at-work-badge">
              <Clock className="w-4 h-4" /> At work for : {workDuration(emp?.join_date)}
            </div>
          </div>

          <div className="grid grid-cols-3 rounded-lg overflow-hidden bg-primary text-primary-foreground" data-testid="employee-quickstats">
            <div className="p-4 text-center border-r border-primary-foreground/20">
              <div className="font-heading text-lg font-bold font-mono">{presentThisMonth}/{daysInMonth}</div>
              <div className="text-[10px] uppercase tracking-wider text-primary-foreground/80 mt-1">Attendance</div>
            </div>
            <div className="p-4 text-center border-r border-primary-foreground/20">
              <div className="font-heading text-lg font-bold font-mono">{stats.my_total_leaves ?? 0}/4</div>
              <div className="text-[10px] uppercase tracking-wider text-primary-foreground/80 mt-1">Leaves</div>
            </div>
            <div className="p-4 text-center">
              <div className="font-heading text-lg font-bold font-mono">0</div>
              <div className="text-[10px] uppercase tracking-wider text-primary-foreground/80 mt-1">Awards</div>
            </div>
          </div>
        </div>

        {/* Middle details column */}
        <div className="lg:col-span-5 space-y-6">
          <SectionCard icon={User} title="Personal Details" testid="section-personal">
            <Row label="Name" value={emp?.name || user?.name} />
            <Row label="Father's Name" value={emp?.father_name} />
            <Row label="Date of Birth" value={fmtDate(emp?.date_of_birth)} />
            <Row label="Gender" value={emp?.gender} />
            <Row label="Email" value={emp?.email || user?.email} />
            <Row label="PRAN" value={emp?.pran} />
            <Row label="Local Address" value={emp?.local_address || emp?.address} />
            <Row label="Permanent Address" value={emp?.permanent_address || emp?.address} />
          </SectionCard>

          <SectionCard icon={Building2} title="Department Details" testid="section-department">
            <Row label="Employee ID" value={emp?.employee_code} />
            <Row label="Department" value={emp?.department} />
            <Row label="Designation" value={emp?.designation} />
            <Row label="Date of Joining" value={fmtDate(emp?.join_date)} />
            <Row label="Salary ( ₹ )" value={emp?.salary ? `Basic : ${Number(emp.salary).toLocaleString("en-IN")} ₹` : "—"} />
          </SectionCard>

          <SectionCard icon={Landmark} title="Bank Details" testid="section-bank">
            <Row label="Account Holder Name" value={emp?.bank_account_holder} />
            <Row label="Account Number" value={emp?.bank_account_number} />
            <Row label="Bank Name" value={emp?.bank_name} />
            <Row label="IFSC Code" value={emp?.ifsc} />
          </SectionCard>
        </div>

        {/* Right column */}
        <div className="lg:col-span-4 space-y-6">
          <SectionCard icon={Bell} title="Notice Board" testid="section-notice">
            <EmptyPanel text="No Notice" />
          </SectionCard>
          <SectionCard icon={CalendarDays} title="Upcoming Holidays" testid="section-holidays">
            <EmptyPanel text="No Holiday" />
          </SectionCard>
          <SectionCard icon={Trophy} title="Awards" testid="section-awards">
            <EmptyPanel text="No Award" />
          </SectionCard>
        </div>
      </div>

      {/* Attendance calendar */}
      <div className="bg-card rounded-lg border border-border overflow-hidden" data-testid="attendance-calendar">
        <div className="bg-[#eaf3fb] px-4 py-3 flex items-center justify-between text-sm">
          <span className="font-semibold text-foreground">Last Absent</span>
          <span className="px-3 py-1 rounded bg-destructive text-destructive-foreground text-xs font-semibold" data-testid="last-absent-badge">Never</span>
        </div>
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-2xl font-bold text-foreground">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => setCursor(new Date())} data-testid="cal-today" className="px-4 py-1.5 rounded-md bg-secondary text-foreground text-sm font-medium hover:bg-muted transition-colors">today</button>
              <button onClick={() => shift(-1)} data-testid="cal-prev" className="p-1.5 rounded-md bg-secondary hover:bg-muted transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => shift(1)} data-testid="cal-next" className="p-1.5 rounded-md bg-secondary hover:bg-muted transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid grid-cols-7 border-l border-t border-border">
            {WEEKDAYS.map((w) => (
              <div key={w} className="border-r border-b border-border bg-secondary/50 py-2 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">{w}</div>
            ))}
            {cells.map((d, i) => {
              const dateStr = d ? `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}` : null;
              const isToday = dateStr === todayStr;
              const isPresent = dateStr && presentDates.has(dateStr);
              return (
                <div key={i} className={`border-r border-b border-border min-h-[72px] p-2 text-right text-sm relative ${d ? "" : "bg-secondary/20"}`}>
                  {d && (
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${isToday ? "bg-primary text-primary-foreground font-bold" : "text-muted-foreground"}`}>{d}</span>
                  )}
                  {isPresent && <span className="absolute left-2 top-2 w-2 h-2 rounded-full bg-[#2f7d5b]" title="Present" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
